import { useCallback, useEffect, useRef, useState } from "react";

import { useCurrentUser } from "context/UserContext";
import { FiAlertCircle, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import { createBugReport } from "services/bugService";

import "./BugReportFab.css";

const BugReportFab = () => {
  const { currentUser } = useCurrentUser();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [description, setDescription] = useState("");
  const [title, setTitle] = useState("");
  const [reporterName, setReporterName] = useState("");

  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  const resetForm = useCallback(() => {
    setDescription("");
    setTitle("");
    setReporterName("");
  }, []);

  const handleOpen = () => {
    setReporterName(currentUser?.displayName || "");
    setOpen(true);
  };

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetForm();
  };

  useEffect(() => {
    if (open && descriptionRef.current) {
      descriptionRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submitting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    setSubmitting(true);
    try {
      await createBugReport({
        description: description.trim(),
        title: title.trim(),
        reporterName: reporterName.trim(),
        reporterEmail: currentUser?.email || "",
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      });

      toast.success("Bug report submitted. Thank you!");
      setOpen(false);
      resetForm();
    } catch {
      toast.error("Failed to submit bug report. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentUser) return null;

  return (
    <>
      <button
        className="bug-fab"
        onClick={handleOpen}
        aria-label="Report a bug"
        title="Report a bug"
      >
        <FiAlertCircle />
      </button>

      {open && (
        <div className="bug-modal-overlay" onClick={handleClose}>
          <div className="bug-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bug-modal__header">
              <h2>Report a Bug</h2>
              <button
                className="bug-modal__close"
                onClick={handleClose}
                aria-label="Close"
              >
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="bug-modal__body">
                <div className="form-group">
                  <label htmlFor="bug-description">What went wrong? *</label>
                  <textarea
                    id="bug-description"
                    ref={descriptionRef}
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe the issue you encountered..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="bug-title">Title</label>
                  <input
                    id="bug-title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Short summary (optional)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="bug-reporter">Your name</label>
                  <input
                    id="bug-reporter"
                    type="text"
                    value={reporterName}
                    onChange={(e) => setReporterName(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="bug-modal__footer">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={handleClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting || !description.trim()}
                >
                  {submitting ? "Submitting..." : "Submit Bug"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default BugReportFab;
